import React from 'react';
import { SizeMeProps } from 'react-sizeme';
import {
  RoomJSONStructureItem,
  RoomStructureContent as RoomStructureContentSchema,
  RoomStructureFolder,
  RoomStructureHeader,
} from '../../../definitions/schemas';
import { shouldDisplayInFullWidth } from '../../../lib/helper/RoomStructureHelper';
import { RoomContentContextProps } from '../../../lib/RoomContentContext';
import { RoomContentMode } from '../../../enums/RoomLayout';
import { SimplifiedTrackingProps } from '../../Rooms/Tracking/types';
import { EventProperty } from '../../../tools/Analytics';
import RoomStructureContent from './RoomStructureContent';

export interface BuildViewStructureNodesConfig {
  /** Ordered structure items (headers, contents, folders) to render. */
  structureItems: RoomJSONStructureItem[];
  /** Matched data the items reference. */
  structureContents: RoomStructureContentSchema[];
  structureHeaders: RoomStructureHeader[];
  structureFolders?: RoomStructureFolder[];

  contentMode: RoomContentMode;
  contextProps: RoomContentContextProps;
  referrer: string;
  roomId: string;
  sizeMeProps: SizeMeProps;
  trackingProperties: EventProperty;

  isBuyerCopilotEnabledForRoom?: boolean;
  /** Stable prefix to namespace React keys (e.g. `structure` vs `folder-structure`). */
  keyPrefix?: string;
  onFolderClick?: (folderId: string) => void;

  // --- Optional view-only extras (used by the main room view, omitted by the
  // simpler folder view) ---
  assignRef?: (id: string) => (el: HTMLDivElement) => void;
  hideHeader?: boolean;
  onCopyAssetLink?: (sectionId: string) => void;
  theaterDeepLinkSectionId?: string;
  /** Used by both the main room view and the folder view to enable content tracking. */
  TrackingProps?: SimplifiedTrackingProps;
  /** RoomContentDetailsProps overrides merged into the defaults below. */
  actionable?: boolean;
  enableDocumentViewportDetection?: boolean;
  hideContactInformation?: boolean;
  renderContentAcl?: (item: RoomJSONStructureItem) => React.ReactNode;
}

const buildViewStructureNodes = (
  config: BuildViewStructureNodesConfig
): JSX.Element[] => {
  const {
    structureItems,
    structureContents,
    structureHeaders,
    structureFolders,
    contentMode,
    contextProps,
    referrer,
    roomId,
    sizeMeProps,
    trackingProperties,
    isBuyerCopilotEnabledForRoom,
    keyPrefix = 'structure',
    onFolderClick,
    assignRef,
    hideHeader = false,
    onCopyAssetLink,
    theaterDeepLinkSectionId,
    TrackingProps,
    actionable = true,
    enableDocumentViewportDetection = false,
    hideContactInformation = false,
    renderContentAcl,
  } = config;

  return structureItems.map((item, index) => {
    const { id, type } = item;

    return (
      <RoomStructureContent
        assignRef={assignRef}
        contextProps={contextProps}
        fullWidth={shouldDisplayInFullWidth(item)}
        hideHeader={hideHeader}
        index={index}
        isBuyerCopilotEnabledForRoom={isBuyerCopilotEnabledForRoom}
        itemId={id}
        key={`${keyPrefix}-${type}-${id}`}
        mode={contentMode}
        onCopyAssetLink={onCopyAssetLink}
        onFolderClick={onFolderClick}
        referrer={referrer}
        renderContentAcl={renderContentAcl?.(item)}
        RoomContentDetailsProps={{
          actionable,
          enableDocumentViewportDetection,
          hideContactInformation,
          RoomEmbeddableContentProps: { hasBorder: true },
          roomId,
          trackingProperties,
        }}
        sizeMeProps={sizeMeProps}
        structureContents={structureContents}
        structureHeaders={structureHeaders}
        structureFolders={structureFolders}
        theaterDeepLinkSectionId={theaterDeepLinkSectionId}
        TrackingProps={TrackingProps}
        type={type}
      />
    );
  });
};

export default buildViewStructureNodes;
